import { get, type Attendance } from "../api.js";

/**
 * WMF-SIte's live Amelia outcome for one appointment, with the participant-
 * specific mutations the signed-in tutor may safely offer.
 *
 * `GET /api/v1/appointments/{id}/lesson-outcome` (WMF-SIte #354). The flags are
 * advisory: the status mutation rechecks authority and safeguards, so a stale
 * read can only produce a refusal, never a wrong write.
 */
export interface LessonOutcome {
  appointment_id: number;
  provider_id: number;
  service_id: number;
  appointment_status: string;
  booking_statuses: Array<{
    booking_id: number;
    customer_id: number;
    status: string;
    persons: number;
  }>;
  participant_count: number;
  active_participant_count: number;
  mutation_scope: "single_participant" | "shared" | "unknown";
  can_mark_no_show: boolean;
  can_undo_no_show: boolean;
  can_cancel: boolean;
}

/** What Amelia currently says happened to the lesson, in the Hub's terms. */
export type AmeliaAttendance = "no-show" | "canceled" | "active" | "other";

export function getLessonOutcome(appointmentId: number): Promise<LessonOutcome> {
  return get<LessonOutcome>(`/staff-api/api/v1/appointments/${appointmentId}/lesson-outcome`);
}

function normalised(status: string): string {
  const value = status.toLowerCase();
  if (value === "no-show" || value === "no_show" || value === "noshow") return "no-show";
  if (value === "cancelled") return "canceled";
  return value;
}

/**
 * What Amelia says about *this pupil's* place in the lesson.
 *
 * A shared appointment carries one status per participant, so another
 * participant's cancellation or no-show says nothing about this note. The
 * pupil is identified by their own Amelia booking id; when it is absent or not
 * in the response, nothing can be proved and the answer is `unknown`, which
 * never overwrites a stored absence and never writes a new one.
 *
 * The appointment's own status counts only where it applies to everyone: a
 * cancelled appointment is cancelled for every participant, but an
 * appointment-level no-show is this pupil's only when they are the sole
 * participant.
 */
export function ameliaAttendance(
  outcome: LessonOutcome,
  ameliaBookingId: number | null,
): AmeliaAttendance | "unknown" {
  if (ameliaBookingId === null) return "unknown";
  const own = outcome.booking_statuses.find((booking) => booking.booking_id === ameliaBookingId);
  if (own === undefined) return "unknown";
  const appointment = normalised(outcome.appointment_status);
  const ownStatus = normalised(own.status);
  if (ownStatus === "no-show") return "no-show";
  if (ownStatus === "canceled" || appointment === "canceled") return "canceled";
  if (appointment === "no-show" && outcome.mutation_scope === "single_participant") return "no-show";
  const live = (status: string) => status === "approved" || status === "pending";
  return live(ownStatus) && live(appointment) ? "active" : "other";
}

/** What the note stores, given what Amelia says and what the tutor chose. */
export function attendanceToSave(
  amelia: AmeliaAttendance | "unknown",
  stored: Attendance | "",
): Attendance | null {
  if (amelia === "no-show") return "absent";
  if (amelia === "canceled") return "cancelled";
  const noteOnly = stored === "present" || stored === "late" ? stored : null;
  // If Amelia cannot be read, never silently erase an earlier absence.
  if (amelia === "unknown" && (stored === "absent" || stored === "cancelled")) return stored;
  return noteOnly;
}
